import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { useOpenWatermarkOffer } from '../useOpenWatermarkOffer';
import { useWatermarkSurface } from '../useWatermarkSurface';
import { watermarkFileMessages as messages } from './watermarkFileMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the upsell; each part uses its own slice of it */
import styles from '../WatermarkUpsell.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface WatermarkFlagProps {
  /** `inline`: next to a label (a Posts row); the default sits on the corner of a thumbnail. */
  inline?: boolean;
  className?: string;
}

/**
 * The "Watermark" flag of a file (design: trial-upsell a4 `.tu-flag`), in the place of the silent
 * preview icon. A click opens the offer. It is a `span` with the button role, because a thumbnail
 * is a button itself and a button may not hold a button. Hidden on a paid plan and for the demo.
 */
const WatermarkFlagComponent: React.FC<WatermarkFlagProps> = ({ inline = false, className }) => {
  const intl = useIntl();
  const { applies } = useWatermarkSurface();
  const openOffer = useOpenWatermarkOffer();
  const open = useCallback(
    (event: React.SyntheticEvent) => {
      // The thumbnail or the row under the flag must not open too.
      event.stopPropagation();
      event.preventDefault();
      openOffer('banner');
    },
    [openOffer]
  );
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        open(event);
      }
    },
    [open]
  );
  if (!applies) {
    return null;
  }
  return (
    <span
      role="button"
      tabIndex={0}
      className={clsx(styles.flag, inline && styles.flagInline, className)}
      onClick={open}
      onKeyDown={onKeyDown}
      data-testid="watermark-flag"
    >
      {intl.formatMessage(messages.flag)}
    </span>
  );
};

export const WatermarkFlag = TypedMemo(WatermarkFlagComponent);
